import { Injectable } from '@angular/core';
import { AppService } from '../../services/app.service';
import { HttpClient } from '@angular/common/http';
import { FILTER, TIMEZONE_KEY } from 'app/const';
import { LocalFilter } from 'app/models/local-filter.model';
import { TipoFilterEnum } from 'enums/tipo-filter.enum';
import { LOCALE_KEY } from '../../const';
import { deleteCookieLocal } from 'app/utils/cookie.utils';
import {toBinaryStr} from "app/commons/toBinaryStr";
import { AbPostHog } from 'app/abstract/services/posthog.abstract';
import { PostHogEventEnum } from 'enums/posthog-event.enum';
import { consumirLogoutEmitido } from 'app/services/posthog/posthog-identidade';
import { clienteAnonimo } from 'app/services/posthog/posthog-client';
import { AbFeatureFlag } from 'app/abstract/services/feature-flag.abstract';

export const TOKEN_KEY = "token";
const USUARIO_KEY = "user_instance";
const PESSOA_PRE_CADASTRO_KEY = "pessoa_pre_cadastro_instance";
const MORADORES_PRE_CADASTRO_KEY = "moradores_pre_cadastro_instance";
const UNIDADES_PRE_CADASTRO_KEY = "unidades_pre_cadastro_instance";
const FOTO_PESSOA_LOGADA_KEY = "foto_pessoa";
const PRIMEIRO_ACESSO_KEY = "primeiro_acesso";
const CARREGAR_CONTAS = "carregar_contas";
const NOME_PESSOA = "nome_pessoa";
const SKIN = "skin";
export const IID = "iid";
export const INTEGRADOR_UUID = "INTEGRADOR_UUID";
const TOKEN_KEY_LGPD = "token_lgpd";
const SENHA_MODAL = "senha_modal";

@Injectable({providedIn:'root'})
export class AuthService extends AppService {

  constructor(http: HttpClient, private featureFlagService: AbFeatureFlag, private posthog: AbPostHog) {
    super(http);
  }

  private _carregaBarload: boolean = true;

  get carregaBarload(): boolean {
    return this._carregaBarload;
  }

  set carregaBarload(b: boolean) {
    this._carregaBarload = b;
  }

  getAccessToken() {
    return this.getToken().access_token;
  }

  getRefreshToken() {
    return this.getToken().refresh_token;
  }

  private getToken() {
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (token) {
      try {
        const parsed = JSON.parse(token);
        // JSON.parse pode retornar primitivos válidos (número, string); garante retorno de objeto.
        return typeof parsed === 'object' && parsed !== null ? parsed : {};
      } catch {
        // Storage corrompido (ex: valor não-JSON gravado por um terceiro) — trata como ausente.
        return {};
      }
    }
    return {};
  }

  isAuthenticated() {
    return this.getAccessToken() != null;
  }

  saveToken(token) {
    const old_token_refresh = this.getToken()?.refresh_token;
    const new_refresh_token = token.refresh_token ? token.refresh_token : old_token_refresh;
    sessionStorage.setItem(TOKEN_KEY, JSON.stringify({...token, refresh_token: new_refresh_token}));
  }

  saveUsuarioStorage(usuario) {
    sessionStorage.setItem(USUARIO_KEY, btoa(toBinaryStr(JSON.stringify(usuario))));
  }

  savePessoaPreCadastro(pessoa){
    sessionStorage.setItem(PESSOA_PRE_CADASTRO_KEY, btoa(encodeURIComponent(toBinaryStr(JSON.stringify(pessoa)))));
  }

  saveMoradoesPreCadastro(moradores){
    sessionStorage.setItem(MORADORES_PRE_CADASTRO_KEY, btoa(toBinaryStr(JSON.stringify(moradores))));
  }

  saveUnidadesPreCadastro(unidades){
    sessionStorage.setItem(UNIDADES_PRE_CADASTRO_KEY, btoa(toBinaryStr(JSON.stringify(unidades))));
  }

  cleanToken() {
    sessionStorage.removeItem(TOKEN_KEY);
  }

  cleanPessoaPreCadastroStorage() {
    sessionStorage.removeItem(PESSOA_PRE_CADASTRO_KEY);
  }

  cleanUnidadePreCadastroStorage() {
    sessionStorage.removeItem(UNIDADES_PRE_CADASTRO_KEY);
  }

  cleanMoradoresPreCadastroStorage() {
    sessionStorage.removeItem(MORADORES_PRE_CADASTRO_KEY);
  }

  cleanTimeZoneStorage() {
    sessionStorage.removeItem(TIMEZONE_KEY);
  }

  getMoradoresPreCadastroStorage(): any {
    const moradores = sessionStorage.getItem(MORADORES_PRE_CADASTRO_KEY);
    if (!moradores) {
      return null;
    }
    return JSON.parse(atob(moradores));
  }

  getPessoaPreCadastroStorage(): any {
    const pessoa = sessionStorage.getItem(PESSOA_PRE_CADASTRO_KEY);
    if (!pessoa) {
      return null;
    }
    return JSON.parse(decodeURIComponent(atob(pessoa)));
  }

  getUnidadesPreCadastroStorage(): any {
    const pessoa = sessionStorage.getItem(UNIDADES_PRE_CADASTRO_KEY);
    if (!pessoa) {
      return null;
    }
    return JSON.parse(atob(pessoa));
  }

  getUsuarioStorage(): any {
    const user = sessionStorage.getItem(USUARIO_KEY);
    if (!user) {
      return null;
    }
    return JSON.parse(atob(user));
  }

  saveFotoPessoaLogada(fotoBase64) {
    sessionStorage.setItem(FOTO_PESSOA_LOGADA_KEY, JSON.stringify(fotoBase64));
  }

  getFotoPessoaLogada() {
    const foto = sessionStorage.getItem(FOTO_PESSOA_LOGADA_KEY);
    if (!foto) {
      return null;
    }
    return JSON.parse(foto);
  }

  logout() {
    // O marcador é consumido sempre, antes do guard de anonimato: avaliá-lo em curto-circuito
    // deixaria um marcador velho vivo para o próximo logout.
    const logoutJaEmitido = consumirLogoutEmitido();
    if (!logoutJaEmitido && !clienteAnonimo()) {
      this.posthog.capture(PostHogEventEnum.LOGOUT_REALIZADO);
    }
    this.posthog.reset();
    this.featureFlagService.limparCapabilitiesPrivadas();
    this.featureFlagService.limparCapabilitiesConta();
    sessionStorage.clear();
    deleteCookieLocal();
  }

  private possuiSessaoAtiva(): boolean {
    return sessionStorage.getItem(USUARIO_KEY) !== null;
  }

  getPrimeiroAcesso() {
    const ret = JSON.parse(sessionStorage.getItem(PRIMEIRO_ACESSO_KEY));
    return ret;
  }

  savePrimeiroAcesso(primeiroAcesso: boolean) {
    sessionStorage.setItem(PRIMEIRO_ACESSO_KEY, JSON.stringify(primeiroAcesso));
  }

  saveCarregarConta(carregar: boolean) {
    sessionStorage.setItem(CARREGAR_CONTAS, JSON.stringify(carregar));
  }

  getCarregarConta() {
    const ret = JSON.parse(sessionStorage.getItem(CARREGAR_CONTAS));
    return ret;
  }

  saveNomePessoa(nome: string) {
    sessionStorage.setItem(NOME_PESSOA, JSON.stringify(nome));
  }

  getNomePessoa() {
    const ret = JSON.parse(sessionStorage.getItem(NOME_PESSOA));
    return ret;
  }

  saveFilter(localFilter: LocalFilter) {
    let localFilters: Array<LocalFilter> = this.getFilter();
    if (localFilters) {
      localFilters.forEach(local => {
        if (local.tipoFilter == localFilter.tipoFilter)
          localFilters.splice(localFilters.indexOf(local), 1);

        localFilters.push(localFilter)
        sessionStorage.setItem(FILTER, JSON.stringify(localFilters));
      });
      return;
    }

    localFilters = [];
    localFilters.push(localFilter)
    sessionStorage.setItem(FILTER, JSON.stringify(localFilters));
  }

  getFilter() {
    const ret = JSON.parse(sessionStorage.getItem(FILTER));
    return ret;
  }

  cleanFilter() {
    sessionStorage.removeItem(FILTER);
  }

  cleanTipoFilter(tipoFilter: TipoFilterEnum) {
    const filters: Array<LocalFilter> = this.getFilter();

    if (filters) {
      filters.forEach(local => {
        if (local.tipoFilter == tipoFilter) {
          const index = filters.indexOf(local);
          filters.splice(index, 1);
        }
      });
      sessionStorage.setItem(FILTER, JSON.stringify(filters));
      return
    }
    sessionStorage.removeItem(FILTER);
  }

  saveSkin(skin: any) {
    delete skin["bannerLoginWeb"]
    sessionStorage.setItem(SKIN, JSON.stringify(skin));
  }

  getSkin() {
    const ret = JSON.parse(sessionStorage.getItem(SKIN));
    return ret;
  }

  cleanSkin() {
    sessionStorage.removeItem(SKIN);
  }

  saveTokenLGPD() {
    sessionStorage.setItem(TOKEN_KEY_LGPD, JSON.stringify(this.getToken()));
    this.cleanToken();
  }

  private getTokenLGPD() {
    const token = sessionStorage.getItem(TOKEN_KEY_LGPD);
    if (token) {
      return JSON.parse(token);
    }
    return null;
  }

  cleanTokenLGPD() {
    if (this.getTokenLGPD() == null) return;
    this.saveToken(this.getTokenLGPD())
    sessionStorage.removeItem(TOKEN_KEY_LGPD);
  }

  saveLocale(locale: any) {
    localStorage.setItem(LOCALE_KEY, locale);
  }

  getLocale() {
    return localStorage.getItem(LOCALE_KEY);
  }

  clearLocale() {
    localStorage.removeItem(LOCALE_KEY);
  }

  getTimeZone() {
    return JSON.parse(sessionStorage.getItem(TIMEZONE_KEY));
  }

  isIntegrador(): boolean {
    const user = this.getUsuarioStorage();
    return !!user?.integrador;
  }

  saveStoredPassword(value) {
    this.clearStoredPassword();
    sessionStorage.setItem(SENHA_MODAL, value);
  }

  getStoredPassword(): string | null {
    const senha = sessionStorage.getItem(SENHA_MODAL);
    return senha && senha.length ? senha : null;
  }

  clearStoredPassword(): void {
    sessionStorage.removeItem(SENHA_MODAL);
  }
}
